import React, { useState, useEffect } from 'react';
import InstrumentListInput from '../InstrumentListInput/InstrumentListInput';
import InstrumentVariedInput from '../InstrumentVariedInput/InstrumentVariedInput';
import PolymerListInput from '../PolymerListInput/PolymerListInput';
import Button from '../../../../Components/Functionality/buttons/Buttons';

const ExploreTab = ({
  selectedInstrument,
  setSelectedInstrument,
  selectedPolymer,
  setSelectedPolymer,
  variableInputValue,
  setVariableInputValue,
  handlePlot,
  showRightColumn,
  SpectralPlotter
}) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  // Track window resize for responsive layout changes
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Determine if we're on mobile
  const isMobile = windowWidth < 768;
  
  // Determine if we need to stack the layout vertically
  const isVerticalLayout = windowWidth < 1024;

  const styles = {
    container: {
      display: 'grid',
      gridTemplateColumns: isVerticalLayout ? '1fr' : '400px 1fr',
      gridTemplateRows: isVerticalLayout ? 'auto auto' : '1fr',
      gap: isVerticalLayout ? '30px' : '60px',
      padding: isMobile ? '12px' : '20px',
      height: isVerticalLayout ? 'auto' : 'calc(100vh - 200px)',
      maxWidth: '100%',
      overflowX: 'hidden'
    },
    searchPanel: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: isMobile ? '16px' : '24px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      width: '100%',
      maxWidth: '100%'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '600',
      color: '#111827',
      marginBottom: isMobile ? '12px' : '20px',
      marginLeft: '0px'
    },
    description: {
      fontSize: '14px',
      color: '#6B7280',
      lineHeight: '1.5',
      marginBottom: isMobile ? '16px' : '24px'
    },
    inputSection: {
      borderBottom: '1px solid #E5E7EB',
      marginBottom: isMobile ? '12px' : '16px',
      paddingBottom: isMobile ? '12px' : '16px'
    },
    inputLabel: {
      fontSize: '14px',
      color: '#374151',
      marginBottom: isMobile ? '8px' : '12px',
      display: 'block'
    },
    selectedDisplay: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: isMobile ? '8px 0' : '12px 0',
      color: '#374151',
      fontSize: '14px', 
      width: '95%'
    },
    buttonContainer: {
      marginTop: isMobile ? '16px' : '24px'
    },
    plotButton: {
      width: isMobile ? '95%' : '100%',
      padding: isMobile ? '10px 12px' : '8px 16px',
      backgroundColor: '#111827',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    },
    resultsPanel: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: isMobile ? '16px' : '24px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      maxWidth: '100%',
      overflowX: 'auto'
    },
    divider: {
      height: '1px',
      backgroundColor: '#E5E7EB',
      margin: isMobile ? '12px 0' : '16px 0'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.searchPanel}>
        <h2 style={styles.sectionTitle}>Spectral Search</h2>
        <p style={styles.description}>
          Browse and analyse polymer spectra from our reference library.
        </p>

        <div style={styles.inputSection}>
          <label style={styles.inputLabel}>Instrument Type</label>
          <InstrumentListInput onChange={value => setSelectedInstrument(value)} />
          {selectedInstrument && (
            <div style={styles.selectedDisplay}>
              <span>Selected</span>
              <span>{selectedInstrument}</span>
            </div>
          )}
        </div>

        {selectedInstrument && (
          <div style={styles.inputSection}>
            <label style={styles.inputLabel}>Spectral Range</label>
            <InstrumentVariedInput 
              instrumentType={selectedInstrument} 
              onChange={value => setVariableInputValue(value)} 
            />
          </div>
        )}

        <div style={styles.inputSection}>
          <label style={styles.inputLabel}>Polymer Type</label>
          <PolymerListInput onChange={value => setSelectedPolymer(value)} />
          {selectedPolymer && (
            <div style={styles.selectedDisplay}>
              <span>Selected</span>
              <span>{selectedPolymer}</span>
            </div>
          )}
        </div>

        <div style={styles.buttonContainer}>
          <Button 
            type='primary'
            onClick={handlePlot}
            style={styles.plotButton}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#1F2937';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = '#111827';
            }}
          >
            View Spectrum
          </Button>
        </div>
      </div>

      {showRightColumn && (
        <div style={styles.resultsPanel}>
          <h2 style={styles.sectionTitle}>
            {selectedPolymer}'s {
              selectedInstrument === "Raman Microscopy" 
                ? "Raman" 
                : selectedInstrument === "Fourier Transform Infrared Spectroscopy" 
                  ? "FTIR" 
                  : selectedInstrument
            } spectrum
          </h2>
          <div style={styles.divider} />
          <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <SpectralPlotter 
              instrumentType={selectedInstrument}
              selectedPolymer={selectedPolymer}
              variableInputValue={variableInputValue}
              pageName='Uploader'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExploreTab;